import { A, useNavigate } from "@solidjs/router";
import { createSignal } from "solid-js";
import { useAuth } from "../auth/AuthContext.tsx";
import { ErrorBanner } from "../components/ErrorBanner.tsx";

/**
 * Self-serve developer signup (#31): create an account, then enroll 2FA on the
 * next step. The TOTP secret ships back once and is handed to /confirm-totp via
 * router state so it isn't persisted on this device.
 */
export function Register() {
  const { client } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [error, setError] = createSignal<unknown | null>(null);
  const [busy, setBusy] = createSignal(false);

  async function onSubmit(e: Event) {
    e.preventDefault();
    if (busy()) return;
    setError(null);
    setBusy(true);
    try {
      const res = await client.register({ email: email(), password: password() });
      navigate("/confirm-totp", {
        replace: true,
        state: {
          accountId: res.accountId,
          totpSecret: res.totpSecret,
          provisioningUri: res.provisioningUri,
        },
      });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="auth-shell">
      <form onSubmit={onSubmit} class="card auth-card">
        <h1>Create your developer account</h1>
        <p class="muted">
          Sign up to issue API keys and build on the agent rail — you'll set up 2FA on the next
          step.
        </p>
        <ErrorBanner error={error()} />
        <label for="email">Email</label>
        <input
          id="email"
          type="email"
          autocomplete="email"
          value={email()}
          onInput={(e) => setEmail(e.currentTarget.value)}
          required
        />
        <label for="password">Password</label>
        <input
          id="password"
          type="password"
          autocomplete="new-password"
          value={password()}
          onInput={(e) => setPassword(e.currentTarget.value)}
          minLength={12}
          required
        />
        <button type="submit" class="primary auth-submit" disabled={busy()}>
          {busy() ? "Creating…" : "Continue"}
        </button>
        <p class="muted auth-alt">
          Already have an account? <A href="/login">Sign in</A>
        </p>
      </form>
    </div>
  );
}
